import { get, isEqual, isObject } from 'radash';
import { proxy, subscribe, unstable_enableOp } from 'valtio';
import { computed } from 'valtio-reactive';

import { defaultDiagram } from '@diagram/data/default-diagram';
import { DiagramSyncService } from '@diagram/services/DiagramSyncService';

import { DIAGRAM_DEFAULT_COLUMNS, DIAGRAM_DEFAULT_ROWS } from '@diagram/diagram-dimensions';

import { DiagramComponentInstance, DiagramConnection } from 'models';

export const diagram = proxy(defaultDiagram());

if (typeof window !== 'undefined') {
    // @ts-ignore
    window.internalSubscribeThing = subscribe;
    // @ts-ignore
    window.internalDiagramProxy = diagram;
}

export const derivedDiagramState = computed({
    grid: () => {
        const rows = diagram.rows ?? DIAGRAM_DEFAULT_ROWS;
        const columns = diagram.columns ?? DIAGRAM_DEFAULT_COLUMNS;

        const grid = Array.from({ length: rows }, () => [
            ...Array<DiagramComponentInstance['id'] | null>(columns).fill(null),
        ]);

        Object.values(diagram.componentInstances).forEach((componentInstance) => {
            const { id, position, colSpan = 1, rowSpan = 1 } = componentInstance;

            if (!position) return;

            // Clamp to the grid so legacy diagrams with partially out-of-bounds components keep loading
            const yEnd = Math.min(position.y + rowSpan, rows);
            const xEnd = Math.min(position.x + colSpan, columns);

            for (let y = Math.max(position.y, 0); y < yEnd; y++) {
                for (let x = Math.max(position.x, 0); x < xEnd; x++) {
                    grid[y][x] = id;
                }
            }
        });

        return grid;
    },
    connectionKeys: () => {
        const connections = diagram.connections;

        return Object.keys(connections);
    },
    connectionsAsArray: () => {
        const connections = diagram.connections;

        return Object.values(connections) as DiagramConnection[];
    },
    connectionAnchorLookup: () => {
        const connections = diagram.connections;

        const lookup: {
            [key: string]: DiagramConnection;
        } = {};

        const createKey = (anchor: DiagramConnection['from']) => {
            return `${anchor.componentInstanceId}-${anchor.edge}-${anchor.offset}`;
        };

        Object.values(connections).forEach((connection) => {
            if (connection.from && connection.to) {
                lookup[createKey(connection.from)] = connection;
                lookup[createKey(connection.to)] = connection;
            }
        });

        return lookup;
    },
    componentInstanceKeys: () => {
        const componentInstances = diagram.componentInstances;

        return Object.keys(componentInstances);
    },
    componentInstancesAsArray: () => {
        const componentInstances = diagram.componentInstances;

        return Object.values(componentInstances) as DiagramComponentInstance[];
    },
});

unstable_enableOp(true);

subscribe(
    diagram,
    (changes) => {
        changes.forEach((change) => {
            const [type, paths, value_, previousValue_] = change;

            const value = value_ === undefined ? undefined : JSON.parse(JSON.stringify(value_));
            const previousValue = previousValue_ === undefined ? undefined : JSON.parse(JSON.stringify(previousValue_));

            const path = paths.join('.');

            if (!path) return;

            if (isArrayLengthChange(paths)) return;

            if (isEqual(value, previousValue)) return;

            if (isObject(value) && Object.keys(value).length > 0 && !process.env.TEST) {
                // Maybe throw an error in the future
                console.warn(
                    `value is an object with keys. Use set(${path}, value) instead. value: ${JSON.stringify(value)}`,
                );
            }

            const skippable = [/textareas\.[0-9]+\.content/].filter((regex) => regex.test(path)).length > 0;

            if (isObject(previousValue) && isObject(value) && Object.keys(value).length > 0 && !skippable) {
                throw new Error(
                    `value cannot be an object to update ${path} Use set(${path}, value) instead or update key-value pairs individually. value: ${JSON.stringify(value)} previousValue: ${JSON.stringify(previousValue)}`,
                );
            }

            if (type === 'set') {
                if (previousValue === undefined) {
                    DiagramSyncService.create(path, value, false);
                } else {
                    DiagramSyncService.updateByKey(path, value, false, previousValue);
                }
            } else if (type === 'delete') {
                // When it's a delete change the 3th item in the change array is the value that is being deleted.
                DiagramSyncService.delete(path, false, value);
            }
        });
    },
    true,
);

const isArrayLengthChange = (path: (string | symbol)[]) => {
    const last = path[path.length - 1];

    if (last !== 'length') {
        return false;
    }

    const parent = path.splice(0, path.length - 1).join('.');

    return Array.isArray(get(diagram, parent));
};
